import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { IntroductionComponent } from '../components/introduction/introduction.component';
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { EopAssistantComponent } from '../components/eop-assistant/eop-assistant.component';
import { PrvNextButtonsComponent } from '../components/prv-next-buttons/prv-next-buttons.component';
import { Icons } from '../../shared/helpers/icons';
import { IUserInfo } from '../../shared/models/contact';
import { IEopDocument } from '../../shared/models/eopDocument';
import { ActivatedRoute, Router } from '@angular/router';
import { UserDataService } from '../../shared/services/userData.service';
import { ESectionTypes } from '../../shared/models/eop_types.enum';
import { EopPlanSlug, buildPlanPageUrl, getBuildingIdFromUrl, getPlanSlugFromUrl } from '../eop-config/eop-page-config';

interface ITrainingLink {
  label: string;
  url: string;
}

interface ITrainingDoc {
  name: string;
  title: string;
  recommendedCompletion: string;
  mdePolicy?: ITrainingLink;
  msStateLaw?: ITrainingLink;
  webLinks: ITrainingLink[];
}

@Component({
  selector: 'sro-training-resources',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    EopAssistantComponent,
    PrvNextButtonsComponent
  ],
  templateUrl: './sro-training-resources.component.html',
  styleUrl: './sro-training-resources.component.css'
})
export class SroTrainingResourcesComponent {
  
  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  currentPageRoute: string | null = null;
  currentPlan: EopPlanSlug = 'al';
  currentBuildingIdFromRoute: string | null = null;
  eopDoc: IEopDocument | null = null;
  eopSectionTypeId = ESectionTypes["Mandatory Training Resources"];
  eopSubSectionTypeId: number | null = null;
  
  trainingDocs: ITrainingDoc[] = [
    {
      name: 'MLEOTA',
      title: "Mississippi Law Enforcement Officers' Academy (MLEOTA)",
      recommendedCompletion: 'October 1st',
      webLinks: [
        { label: 'Multihazard Emergency Planning for Schools Site Index', url: 'https://www.dps.ms.gov/MLEOTA/Advanced/course' }
      ]
    },
    {
      name: 'NASRO / MDE',
      title: 'NASRO 40 hour class with MDE 8 hour legal or MDE SRO',
      recommendedCompletion: 'tba',
      webLinks: [
        { label: 'National Association of School Resource Officers (NASRO) Basic SRO Course', url: 'https://masro.org/training-events/2023/3/13/nasro-basic-sro-course' },
        { label: 'MDE School Safety', url: 'https://www.mdek12.org/OSS/SS' }
      ]
    },
    {
      name: 'ALERRT',
      title: 'Advanced Law Enforcement Rapid Response (ALERRT)',
      recommendedCompletion: 'in last 3 years',
      webLinks: [
        { label: 'Advanced Law Enforcement Rapid Response', url: 'https://alerrt.org/' }
      ]
    },
    {
      name: 'Firearms',
      title: 'Firearms',
      recommendedCompletion: 'Twice a year',
      webLinks: [
        { label: 'NRA Handgun/Shotgun Instructor Development Course', url: 'https://masro.org/training-events/2022/12/19/nra-handgunshotgun-instructor-development-course' }
      ]
    },
    {
      name: 'NIMS ICS',
      title: 'NIMS ICS core courses 100, 200, 700, 800',
      recommendedCompletion: 'tba',
      webLinks: [
        { label: 'ICS and NIMS Courses', url: 'https://training.fema.gov/nims/' }
      ]
    },
    {
      name: 'Multihazard Emergency Planning',
      title: 'IS-362.a-Multihazard Emergency Planning for Schools',
      recommendedCompletion: 'tba',
      webLinks: [
        { label: 'MDE Legal Course - 8 hours', url: 'https://training.fema.gov/programs/schools-multihazard/#item1' }
      ]
    },
    {
      name: 'MDE Legal',
      title: 'MDE 8-Hour Legal Class',
      recommendedCompletion: 'tba',
      webLinks: [
        { label: 'MDE Legal Course - 8 hours', url: 'https://www.mdek12.org/OSS/SS' }
      ]
    },
    {
      name: 'MDE approved In-Service',
      title: '40 hours of MDE approved in-service training for continued certification',
      recommendedCompletion: 'each year',
      webLinks: [
        { label: 'MDE Training', url: 'https://www.mdek12.org/OEE/Training' }
      ]
    }
  ];
  selectedDoc: ITrainingDoc = this.trainingDocs[0];
  
  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string | null = null;
  

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userDataService: UserDataService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.currentPlan = getPlanSlugFromUrl(this.router.url);
    this.currentBuildingIdFromRoute = getBuildingIdFromUrl(this.router.url);
    this.route.paramMap.subscribe(params => {
      this.currentPageRoute = params.get('pageRoute');
    });
  }

  getCurrentRoute(): string | null {
    const url = this.router.url.replace(/\/+$/, ''); // remove trailing slash
    const segments = url.split('/');
    return segments[-1] || segments[segments.length - 1]; // get last segment
  }

  selectDoc(doc: ITrainingDoc) {
    this.selectedDoc = doc;
  }
  
  navigateToPage(route: string): void {
    const buildingId = this.currentBuildingIdFromRoute ?? this.currentBuildingId;
    if (!buildingId) {
      return;
    }

    this.router.navigateByUrl(buildPlanPageUrl(this.currentPlan, buildingId, route));
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

}
